import { useState, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Search, Loader2, Trash2, Download, Send, Plus, History,
  DollarSign, Package, X, FileText, ChevronRight, ListChecks,
  CheckCircle, AlertCircle, Printer, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { exportToCSV, printTable } from '../../lib/printUtils';
import FirstTimeHint from '../../components/FirstTimeHint';

const fmt = (n) => Math.round(n || 0).toLocaleString('es-CL');

export default function CotizadorHistorico() {
  // Material search (from catalog)
  const [matQuery, setMatQuery] = useState('');
  const [matResults, setMatResults] = useState([]);
  const [matTotal, setMatTotal] = useState(0);
  const [matSearching, setMatSearching] = useState(false);
  const [matModalOpen, setMatModalOpen] = useState(false);

  // Price history for selected material
  const [priceModal, setPriceModal] = useState({ open: false, material: null, existingItemId: null });
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceData, setPriceData] = useState({ compras: [], stats: null });

  // Quotation items
  const [items, setItems] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [destinatario, setDestinatario] = useState('');
  const [emailEnvio, setEmailEnvio] = useState('');
  const [generating, setGenerating] = useState(false);
  const searchRef = useRef(null);

  // Bulk search
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkSearching, setBulkSearching] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkPriceChoice, setBulkPriceChoice] = useState({}); // { idx: 'min'|'avg'|'max' }

  // Search materials from catalog (same as Compras)
  const searchMateriales = useCallback(async (q) => {
    if (!q || q.trim().length < 2) return;
    setMatSearching(true);
    try {
      const res = await api.get('/compras/materiales', { params: { q: q.trim(), limit: 750 } });
      setMatResults(res.data.materiales || []);
      setMatTotal(res.data.total_coincidencias || res.data.total || 0);
    } catch {
      toast.error('Error al buscar materiales');
    } finally {
      setMatSearching(false);
    }
  }, []);

  // Load price history for a specific material (optionally linked to an existing quote item)
  const loadPriceHistory = useCallback(async (material, existingItemId = null) => {
    setPriceModal({ open: true, material, existingItemId });
    setPriceLoading(true);
    setPriceData({ compras: [], stats: null });
    try {
      const desc = material.descripcion2 || material.descripcion || '';
      const res = await api.get('/cotizador/precios-material', {
        params: { codmat: material.codigo || '', desc }
      });
      setPriceData(res.data);
    } catch {
      toast.error('Error al cargar precios');
    } finally {
      setPriceLoading(false);
    }
  }, []);

  const addItemFromMaterial = (material) => {
    setItems(prev => [...prev, {
      id: Date.now(),
      descripcion: material.descripcion2 || material.descripcion || '',
      codmat: material.codigo || '',
      precio: 0,
      cantidad: 1,
      unidad: material.unidad_medida || 'UND',
      familia: material.familia_nombre || '',
    }]);
    toast.success('Material agregado — edita el precio');
  };

  const addItemWithPrice = (material, precio, unidad) => {
    if (priceModal.existingItemId) {
      // Update existing item's price
      setItems(prev => prev.map(it => it.id === priceModal.existingItemId ? { ...it, precio: precio || 0 } : it));
      setPriceModal({ open: false, material: null, existingItemId: null });
      toast.success(`Precio actualizado a $${fmt(precio)}`);
    } else {
      // Add new item
      setItems(prev => [...prev, {
        id: Date.now(),
        descripcion: material.descripcion2 || material.descripcion || material.nombre || '',
        codmat: material.codigo || material.codmat || '',
        precio: precio || 0,
        cantidad: 1,
        unidad: unidad || material.unidad_medida || 'UND',
        familia: material.familia_nombre || '',
      }]);
      setPriceModal({ open: false, material: null, existingItemId: null });
      toast.success(`Agregado a $${fmt(precio)}`);
    }
  };

  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(it => it.id === id ? { ...it, [field]: value } : it));
  };
  const removeItem = (id) => setItems(prev => prev.filter(it => it.id !== id));

  // Bulk search
  const handleBulkSearch = async () => {
    const lines = bulkText.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    if (!lines.length) { toast.error('Pega al menos un material'); return; }
    setBulkSearching(true);
    setBulkResults(null);
    setBulkPriceChoice({});
    try {
      const res = await api.post('/cotizador/busqueda-masiva', { items: lines });
      setBulkResults(res.data);
      // Default all found items to 'avg'
      const defaults = {};
      (res.data.results || []).forEach((r, i) => { if (r.found) defaults[i] = 'avg'; });
      setBulkPriceChoice(defaults);
    } catch {
      toast.error('Error en búsqueda masiva');
    } finally {
      setBulkSearching(false);
    }
  };

  const addBulkToQuotation = () => {
    if (!bulkResults) return;
    const newItems = [];
    bulkResults.results.forEach((r, i) => {
      if (!r.found) return;
      const choice = bulkPriceChoice[i] || 'avg';
      const precio = choice === 'min' ? r.min : choice === 'max' ? r.max : r.avg;
      newItems.push({
        id: Date.now() + i,
        descripcion: r.input,
        codmat: '',
        precio,
        cantidad: 1,
        unidad: r.unidad || 'UND',
        familia: '',
        match: r.match,
      });
    });
    setItems(prev => [...prev, ...newItems]);
    setBulkOpen(false);
    toast.success(`${newItems.length} items agregados a la cotización`);
  };

  const totalNeto = items.reduce((sum, it) => sum + (it.precio * it.cantidad), 0);
  const totalIVA = Math.round(totalNeto * 0.19);
  const totalFinal = totalNeto + totalIVA;

  const handleDownloadPDF = async () => {
    if (!items.length) return;
    setGenerating(true);
    try {
      const res = await api.post('/cotizador/generar-pdf', {
        items: items.map(it => ({
          descripcion: it.descripcion, codmat: it.codmat,
          precio: it.precio, cantidad: it.cantidad, unidad: it.unidad,
        })),
        observaciones, destinatario,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cotizacion_historica_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch {
      toast.error('Error al generar PDF');
    } finally {
      setGenerating(false);
    }
  };

  // Carlos (Feb 2026): imprimir y exportar la cotización actual
  const handlePrintCotizacion = () => {
    if (!items.length) {
      toast.warning('No hay items en la cotización para imprimir');
      return;
    }
    printTable({
      title: 'Cotización (precios históricos)',
      subtitle: `${items.length} items${destinatario ? ` · Destinatario: ${destinatario}` : ''} · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Items', value: items.length },
        { label: 'Neto', value: `$${fmt(totalNeto)}` },
        { label: 'IVA 19%', value: `$${fmt(totalIVA)}` },
        { label: 'Total', value: `$${fmt(totalFinal)}`, highlight: true },
      ],
      columns: [
        { key: 'codmat', label: 'Código', mono: true },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'familia', label: 'Familia' },
        { key: 'unidad', label: 'Unidad', align: 'center' },
        { key: 'precio', label: 'P. Unit.', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
        { key: 'cantidad', label: 'Cant.', align: 'center', mono: true, format: (v) => Number(v) || 0 },
        { key: 'total', label: 'Total', align: 'right', mono: true, format: (_v, r) => `$${fmt((r.precio || 0) * (r.cantidad || 0))}` },
      ],
      rows: items,
      totals: [
        { label: 'Neto', value: `$${fmt(totalNeto)}`, span: 6 },
        { label: 'IVA 19%', value: `$${fmt(totalIVA)}`, span: 6 },
        { label: 'TOTAL', value: `$${fmt(totalFinal)}`, span: 6 },
      ],
    });
  };

  const handleExcelCotizacion = () => {
    if (!items.length) {
      toast.warning('No hay items en la cotización para exportar');
      return;
    }
    exportToCSV({
      filename: 'cotizacion_historica',
      title: 'CHIRINO STEEL · Cotización (precios históricos)',
      summary: [
        { label: 'Items', value: items.length },
        { label: 'Destinatario', value: destinatario || '-' },
        { label: 'Email envío', value: emailEnvio || '-' },
        { label: 'Neto (CLP)', value: Math.round(totalNeto) },
        { label: 'IVA 19% (CLP)', value: Math.round(totalIVA) },
        { label: 'Total (CLP)', value: Math.round(totalFinal) },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'codmat', label: 'Código' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'familia', label: 'Familia' },
        { key: 'unidad', label: 'Unidad' },
        { key: 'precio', label: 'Precio Unit. (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'cantidad', label: 'Cantidad', format: (v) => Number(v) || 0 },
        { key: 'total', label: 'Total (CLP)', format: (_v, r) => Math.round((r.precio || 0) * (r.cantidad || 0)) },
      ],
      rows: items,
    });
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="cotizador-historico">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" style={{ color: '#003366' }}>
            <History className="w-6 h-6" /> Cotizador Precios Historicos
          </h1>
          <p className="text-sm text-muted-foreground">Busca en el maestro de materiales, revisa precios de compras anteriores y genera tu cotizacion</p>
        </div>
        {items.length > 0 && (
          <div className="text-right">
            <p className="text-xs text-muted-foreground">{items.length} items</p>
            <p className="text-lg font-bold" style={{ color: '#003366' }}>${fmt(totalFinal)}</p>
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => { setMatModalOpen(true); setMatQuery(''); setMatResults([]); }}
          style={{ backgroundColor: '#003366' }} data-testid="open-material-search-btn">
          <Search className="w-4 h-4 mr-2" /> Buscar Material
        </Button>
        <Button onClick={() => { setBulkOpen(true); setBulkResults(null); setBulkText(''); }}
          variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50"
          data-testid="open-bulk-search-btn">
          <ListChecks className="w-4 h-4 mr-2" /> Cotizacion Masiva
        </Button>
        <Button variant="outline" onClick={() => setItems(prev => [...prev, {
          id: Date.now(), descripcion: '', codmat: '', precio: 0, cantidad: 1, unidad: 'UND', familia: '',
        }])} data-testid="add-manual-item-btn">
          <Plus className="w-4 h-4 mr-1" /> Item Manual
        </Button>
        <Button variant="outline" onClick={handlePrintCotizacion}
          disabled={items.length === 0}
          title="Imprimir cotización"
          data-testid="btn-print-cotizacion">
          <Printer className="w-4 h-4 mr-1" /> Imprimir
        </Button>
        <Button variant="outline" onClick={handleExcelCotizacion}
          disabled={items.length === 0}
          className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
          title="Exportar a Excel"
          data-testid="btn-excel-cotizacion">
          <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
        </Button>
      </div>

      <FirstTimeHint
        hintId="cotizador-historico-intro"
        title="Tip: arma cotizaciones con precios reales"
        description='Busca un material y haz clic en el icono "⏱" junto a un precio para usarlo. Para masivo, pega un listado y deja que el sistema encuentre los mejores precios. Para casos especiales, usa "Item Manual".'
      />

      {/* Quotation Table */}
      <Card>
        <CardContent className="p-0">
          <div className="bg-[#003366] text-white px-3 py-2 flex items-center justify-between rounded-t-lg">
            <span className="text-xs font-bold flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> Cotizacion ({items.length} items)
            </span>
            {items.length > 0 && (
              <Button variant="ghost" size="sm" className="h-6 text-white hover:text-red-200 text-[10px]"
                onClick={() => { if (window.confirm('Limpiar toda la cotizacion?')) setItems([]); }}>
                <Trash2 className="w-3 h-3 mr-1" /> Limpiar
              </Button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-10 h-10 mx-auto mb-2 text-muted-foreground opacity-40" />
              <p className="text-sm text-muted-foreground">Busca materiales desde el maestro y agrega items con precios historicos</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="cotizacion-table">
                <thead>
                  <tr className="border-b bg-gray-50 text-[10px] text-muted-foreground">
                    <th className="px-2 py-1.5 text-left w-6">#</th>
                    <th className="px-2 py-1.5 text-left">Material</th>
                    <th className="px-2 py-1.5 text-right w-24">P. Unit.</th>
                    <th className="px-2 py-1.5 text-center w-16">Cant.</th>
                    <th className="px-2 py-1.5 text-left w-12">Unid.</th>
                    <th className="px-2 py-1.5 text-right w-24">Total</th>
                    <th className="px-2 py-1.5 w-14">Precios</th>
                    <th className="px-2 py-1.5 w-7"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={it.id} className="border-b hover:bg-gray-50">
                      <td className="px-2 py-1 text-muted-foreground">{idx + 1}</td>
                      <td className="px-2 py-1">
                        {it.codmat ? (
                          <span className="font-medium block truncate max-w-[280px]" title={it.descripcion}>
                            {it.descripcion}
                          </span>
                        ) : (
                          <input value={it.descripcion}
                            onChange={(e) => updateItem(it.id, 'descripcion', e.target.value)}
                            placeholder="Descripcion del item"
                            className="w-full border rounded px-1.5 py-0.5 text-xs" />
                        )}
                        {it.familia && <span className="text-[9px] text-muted-foreground">{it.familia}</span>}
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={it.precio}
                          onChange={(e) => updateItem(it.id, 'precio', Math.max(0, Number(e.target.value)))}
                          className="w-full text-right border rounded px-1.5 py-0.5 text-xs font-mono"
                          data-testid={`item-price-${idx}`} />
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={it.cantidad} min="0.1" step="0.1"
                          onChange={(e) => updateItem(it.id, 'cantidad', Math.max(0.1, Number(e.target.value)))}
                          className="w-full text-center border rounded px-1 py-0.5 text-xs"
                          data-testid={`item-qty-${idx}`} />
                      </td>
                      <td className="px-2 py-1 text-[10px] text-muted-foreground">{it.unidad}</td>
                      <td className="px-2 py-1 text-right font-mono font-bold">${fmt(it.precio * it.cantidad)}</td>
                      <td className="px-1 py-1 text-center">
                        <button onClick={() => loadPriceHistory({ codigo: it.codmat || '', descripcion: it.match || it.descripcion, descripcion2: it.match || it.descripcion, unidad_medida: it.unidad }, it.id)}
                          className="text-blue-600 hover:text-blue-800" title="Ver precios historicos"
                          data-testid={`history-btn-${idx}`}>
                          <History className="w-3.5 h-3.5" />
                        </button>
                      </td>
                      <td className="px-1 py-1">
                        <button onClick={() => removeItem(it.id)} className="text-red-400 hover:text-red-600"
                          data-testid={`remove-item-${idx}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {items.length > 0 && (
            <div className="border-t bg-gray-50 p-3">
              <div className="flex justify-end gap-6 text-xs">
                <div className="text-right">
                  <p className="text-muted-foreground">Neto</p>
                  <p className="font-mono font-bold">${fmt(totalNeto)}</p>
                </div>
                <div className="text-right">
                  <p className="text-muted-foreground">IVA 19%</p>
                  <p className="font-mono font-bold">${fmt(totalIVA)}</p>
                </div>
                <div className="text-right">
                  <p className="text-muted-foreground font-bold">TOTAL</p>
                  <p className="font-mono font-bold text-base" style={{ color: '#003366' }}>${fmt(totalFinal)}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Observaciones + Actions */}
      {items.length > 0 && (
        <Card>
          <CardContent className="p-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-medium text-muted-foreground">Destinatario (para PDF)</label>
                <Input value={destinatario} onChange={(e) => setDestinatario(e.target.value)}
                  placeholder="Nombre o empresa destinatario" className="h-8 text-xs" data-testid="cotizador-destinatario" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-muted-foreground">Enviar por email a</label>
                <Input value={emailEnvio} onChange={(e) => setEmailEnvio(e.target.value)}
                  placeholder="correo@ejemplo.cl" type="email" className="h-8 text-xs" data-testid="cotizador-email" />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-medium text-muted-foreground">Observaciones</label>
              <textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value)}
                rows={2} placeholder="Notas adicionales..."
                className="w-full border rounded px-2 py-1.5 text-xs resize-none" data-testid="cotizador-obs" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={handleDownloadPDF}
                disabled={generating} className="h-8" data-testid="cotizador-download-btn">
                {generating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                Descargar PDF
              </Button>
              <Button size="sm" disabled={!emailEnvio}
                style={{ backgroundColor: '#003366' }} className="h-8"
                onClick={() => toast.info('Funcionalidad de envio requiere configuracion Microsoft Graph')}
                data-testid="cotizador-send-btn">
                <Send className="w-3.5 h-3.5 mr-1" /> Enviar por Email
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== MATERIAL SEARCH MODAL (same as Compras) ===== */}
      <Dialog open={matModalOpen} onOpenChange={setMatModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col" data-testid="material-search-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" style={{ color: '#003366' }} /> Buscar Material
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input ref={searchRef} placeholder="Buscar por nombre, codigo, familia..."
                value={matQuery}
                onChange={(e) => {
                  setMatQuery(e.target.value);
                  if (e.target.value.length >= 2) searchMateriales(e.target.value);
                }}
                onKeyDown={(e) => e.key === 'Enter' && searchMateriales(matQuery)}
                className="h-9" autoFocus data-testid="mat-search-input" />
            </div>
          </div>
          {matSearching && (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" /></div>
          )}
          {matResults.length > 0 && (
            <div className="text-xs text-muted-foreground px-1">{matTotal} materiales encontrados</div>
          )}
          <div className="flex-1 overflow-y-auto border rounded divide-y max-h-[50vh]">
            {matResults.length === 0 && !matSearching && matQuery.length >= 2 && (
              <p className="p-4 text-sm text-center text-muted-foreground">Sin resultados</p>
            )}
            {matResults.map((mat) => (
              <div key={mat.codigo || mat.descripcion}
                className="px-3 py-2 hover:bg-blue-50 flex items-center justify-between cursor-pointer group text-sm"
                data-testid={`mat-result-${mat.codigo}`}>
                <div className="flex-1 min-w-0" onClick={() => loadPriceHistory(mat)}>
                  <p className="font-medium truncate">{mat.descripcion2 || mat.descripcion}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {mat.familia_nombre}{mat.subfamilia_nombre ? ` / ${mat.subfamilia_nombre}` : ''} | {mat.unidad_medida}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs opacity-60 group-hover:opacity-100"
                    onClick={(e) => { e.stopPropagation(); loadPriceHistory(mat); }}>
                    <DollarSign className="w-3.5 h-3.5 mr-0.5" /> Precios
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-green-700 opacity-60 group-hover:opacity-100"
                    onClick={(e) => { e.stopPropagation(); addItemFromMaterial(mat); }}>
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMatModalOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== PRICE HISTORY MODAL ===== */}
      <Dialog open={priceModal.open} onOpenChange={(o) => { if (!o) setPriceModal({ open: false, material: null, existingItemId: null }); }}>
        <DialogContent className="max-w-lg max-h-[75vh] flex flex-col" data-testid="price-history-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <DollarSign className="w-5 h-5 text-amber-500" /> Precios Historicos
            </DialogTitle>
          </DialogHeader>
          {priceModal.material && (
            <p className="text-sm font-medium truncate" title={priceModal.material.descripcion2 || priceModal.material.descripcion}>
              {priceModal.material.descripcion2 || priceModal.material.descripcion}
            </p>
          )}

          {priceLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
          ) : priceData.compras.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">
              Sin historial de compras para este material
              {priceModal.material && !priceModal.existingItemId && (
                <div className="mt-3">
                  <Button size="sm" variant="outline" onClick={() => {
                    addItemFromMaterial(priceModal.material);
                    setPriceModal({ open: false, material: null, existingItemId: null });
                  }}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Agregar sin precio
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              {priceData.stats && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  {priceModal.existingItemId && (
                    <p className="col-span-3 text-[10px] text-muted-foreground mb-1">Selecciona un precio para actualizar el item</p>
                  )}
                  <div className="bg-green-50 rounded p-2 cursor-pointer hover:ring-2 ring-green-400 transition-all"
                    onClick={() => addItemWithPrice(priceModal.material, priceData.stats.min, priceData.stats.unidad)}>
                    <p className="text-[10px] text-green-700">Min</p>
                    <p className="text-sm font-bold text-green-700">${fmt(priceData.stats.min)}</p>
                    <p className="text-[9px] text-green-600">{priceModal.existingItemId ? 'click para actualizar' : 'click para usar'}</p>
                  </div>
                  <div className="bg-blue-50 rounded p-2 cursor-pointer hover:ring-2 ring-blue-400 transition-all"
                    onClick={() => addItemWithPrice(priceModal.material, priceData.stats.avg, priceData.stats.unidad)}>
                    <p className="text-[10px] text-blue-700">Promedio</p>
                    <p className="text-sm font-bold text-blue-700">${fmt(priceData.stats.avg)}</p>
                    <p className="text-[9px] text-blue-600">{priceModal.existingItemId ? 'click para actualizar' : 'click para usar'}</p>
                  </div>
                  <div className="bg-red-50 rounded p-2 cursor-pointer hover:ring-2 ring-red-400 transition-all"
                    onClick={() => addItemWithPrice(priceModal.material, priceData.stats.max, priceData.stats.unidad)}>
                    <p className="text-[10px] text-red-700">Max</p>
                    <p className="text-sm font-bold text-red-700">${fmt(priceData.stats.max)}</p>
                    <p className="text-[9px] text-red-600">{priceModal.existingItemId ? 'click para actualizar' : 'click para usar'}</p>
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-y-auto border rounded max-h-[35vh]">
                <table className="w-full text-xs" data-testid="price-history-table">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b text-[10px] text-muted-foreground">
                      <th className="px-2 py-1.5 text-left">Fecha</th>
                      <th className="px-2 py-1.5 text-left">Proveedor</th>
                      <th className="px-2 py-1.5 text-right">Cant.</th>
                      <th className="px-2 py-1.5 text-right">V. Unit.</th>
                      <th className="px-2 py-1.5 text-right">OC</th>
                      <th className="px-2 py-1.5 w-7"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {priceData.compras.map((c, i) => (
                      <tr key={`${c.numero_oc}-${i}`} className="border-b hover:bg-amber-50 group">
                        <td className="px-2 py-1.5">{c.fecha || '-'}</td>
                        <td className="px-2 py-1.5 truncate max-w-[150px]" title={c.proveedor}>{c.proveedor || '-'}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{c.cantidad}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-bold">${fmt(c.valor_unitario)}</td>
                        <td className="px-2 py-1.5 text-right text-muted-foreground">{c.numero_oc}</td>
                        <td className="px-1 py-1.5">
                          <button className="opacity-0 group-hover:opacity-100 text-green-600 hover:text-green-800"
                            onClick={() => addItemWithPrice(priceModal.material, c.valor_unitario, c.unidad_medida)}
                            title="Usar este precio">
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceModal({ open: false, material: null, existingItemId: null })}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ===== BULK SEARCH MODAL ===== */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="bulk-search-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-amber-500" /> Cotizacion Masiva
            </DialogTitle>
          </DialogHeader>

          {!bulkResults ? (
            /* INPUT PHASE */
            <div className="space-y-3 flex-1">
              <p className="text-sm text-muted-foreground">
                Pega tu listado de materiales (un item por linea). El sistema buscara precios historicos para cada uno.
              </p>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={12}
                placeholder={`Ejemplo:\nCañería 12" A106 Gr.B STD\nCodo 90° BW 3"\nTee BW 3"\nPerno Hex 1/2 x 2\nPlancha ASTM A-36 6mm`}
                className="w-full border rounded px-3 py-2 text-sm font-mono resize-none"
                data-testid="bulk-text-input"
              />
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">
                  {bulkText.split('\n').filter(l => l.trim()).length} items detectados (max 100)
                </span>
                <Button onClick={handleBulkSearch} disabled={bulkSearching || !bulkText.trim()}
                  style={{ backgroundColor: '#003366' }} data-testid="bulk-search-btn">
                  {bulkSearching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Buscar Precios
                </Button>
              </div>
            </div>
          ) : (
            /* RESULTS PHASE */
            <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
              {/* Summary */}
              <div className="flex gap-3 text-sm">
                <span className="flex items-center gap-1 text-green-700">
                  <CheckCircle className="w-4 h-4" /> {bulkResults.found} encontrados
                </span>
                {bulkResults.not_found > 0 && (
                  <span className="flex items-center gap-1 text-red-600">
                    <AlertCircle className="w-4 h-4" /> {bulkResults.not_found} sin match
                  </span>
                )}
                <span className="text-muted-foreground ml-auto text-xs">
                  Click en Min/Prom/Max para elegir precio por item
                </span>
              </div>

              {/* Results table */}
              <div className="flex-1 overflow-y-auto border rounded">
                <table className="w-full text-xs" data-testid="bulk-results-table">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="border-b bg-gray-50 text-[10px] text-muted-foreground">
                      <th className="px-2 py-1.5 text-left w-6">#</th>
                      <th className="px-2 py-1.5 text-left">Tu Material</th>
                      <th className="px-2 py-1.5 text-left">Match Encontrado</th>
                      <th className="px-2 py-1.5 text-right w-16">Min</th>
                      <th className="px-2 py-1.5 text-right w-16">Prom</th>
                      <th className="px-2 py-1.5 text-right w-16">Max</th>
                      <th className="px-2 py-1.5 text-center w-10">#</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkResults.results.map((r, i) => (
                      <tr key={`bulk-${i}`} className={`border-b ${r.found ? 'hover:bg-gray-50' : 'bg-red-50/50'}`}>
                        <td className="px-2 py-1.5 text-muted-foreground">{i + 1}</td>
                        <td className="px-2 py-1.5">
                          <span className="font-medium block truncate max-w-[180px]" title={r.input}>{r.input}</span>
                        </td>
                        <td className="px-2 py-1.5">
                          {r.found ? (
                            <span className="block truncate max-w-[200px] text-green-800" title={r.match}>{r.match}</span>
                          ) : (
                            <span className="text-red-500 italic">Sin match</span>
                          )}
                        </td>
                        {r.found ? (
                          <>
                            <td className="px-2 py-1.5 text-right">
                              <button onClick={() => setBulkPriceChoice(prev => ({ ...prev, [i]: 'min' }))}
                                className={`font-mono px-1 rounded transition-all ${bulkPriceChoice[i] === 'min' ? 'bg-green-200 font-bold text-green-800 ring-1 ring-green-400' : 'hover:bg-green-100'}`}>
                                ${fmt(r.min)}
                              </button>
                            </td>
                            <td className="px-2 py-1.5 text-right">
                              <button onClick={() => setBulkPriceChoice(prev => ({ ...prev, [i]: 'avg' }))}
                                className={`font-mono px-1 rounded transition-all ${bulkPriceChoice[i] === 'avg' ? 'bg-blue-200 font-bold text-blue-800 ring-1 ring-blue-400' : 'hover:bg-blue-100'}`}>
                                ${fmt(r.avg)}
                              </button>
                            </td>
                            <td className="px-2 py-1.5 text-right">
                              <button onClick={() => setBulkPriceChoice(prev => ({ ...prev, [i]: 'max' }))}
                                className={`font-mono px-1 rounded transition-all ${bulkPriceChoice[i] === 'max' ? 'bg-red-200 font-bold text-red-800 ring-1 ring-red-400' : 'hover:bg-red-100'}`}>
                                ${fmt(r.max)}
                              </button>
                            </td>
                            <td className="px-2 py-1.5 text-center text-muted-foreground">{r.count}</td>
                          </>
                        ) : (
                          <td colSpan={4} className="px-2 py-1.5 text-center text-red-400 text-[10px]">
                            Sin historial de compras
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center">
                <Button variant="ghost" size="sm" onClick={() => setBulkResults(null)}>
                  Volver a editar
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancelar</Button>
                  <Button onClick={addBulkToQuotation} disabled={bulkResults.found === 0}
                    style={{ backgroundColor: '#003366' }} data-testid="bulk-add-btn">
                    <Plus className="w-4 h-4 mr-1" /> Agregar {bulkResults.found} items a Cotizacion
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
